import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { ApiService, QueryParam } from '../../../../services/api.services';
import {
	DataSetComponent,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../../../components/data-set/data-set.component';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { DeviceNote, DeviceNotePhoto } from 'src/app/models/data/device';
import { User } from '../../../../models/data/user';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { PhotoGalleryComponent } from '../../../../components/photo-gallery/photo-gallery.component';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { TimeZoneService } from 'src/app/services/timezone.service';
import { ApiLoadingKeys } from 'src/app/services/api-mock.service';
import { DataSetSearchComponent } from '../../../../components/data-set/data-set-search.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { DataSetTableComponent } from '../../../../components/data-set/data-set-table.component';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { DatePipe, formatDate } from '@angular/common';
import { UserNamePipe } from '../../../../components/pipes';
import { DataSetLoadingRowComponent } from '../../../../components/data-set/data-set-loading-row.component';
import { MatSelect, MatOption } from '@angular/material/select';
import {
	MatDatepicker,
	MatDatepickerInput,
	MatDatepickerToggle,
} from '@angular/material/datepicker';
import { MatSuffix } from '@angular/material/form-field';
import { map } from 'rxjs/operators';

@Component({
	selector: 'app-device-notes',
	templateUrl: './device-notes.component.html',
	styleUrls: ['./device-notes.component.scss'],
	imports: [
		DataSetComponent,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		MatSelect,
		MatOption,
		MatDatepickerInput,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepicker,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		MatIcon,
		MatTooltip,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		DatePipe,
		UserNamePipe,
		DataSetLoadingRowComponent,
	],
})
export class DeviceNotesComponent implements AfterViewInit, OnDestroy {
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;
	@ViewChild(MatSort, { static: false }) sort!: MatSort;
	@ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

	deviceId = this.route.snapshot.params.id;

	notePhotosTooltip = $localize`:@@deviceNotes-notePhotosTooltip:Note Photos`;

	dataSource = new MatTableDataSource<{
		deviceNote: DeviceNote;
		user: User;
		deviceNotePhotos: DeviceNotePhoto[];
		arrowListener?: () => void;
		contractedNotes?: boolean;
	}>();
	displayedColumns: string[] = [
		'addedBy',
		'note',
		'dateAdded',
		'noteType',
		'photos',
	];

	filterLabels = {
		defaultPlaceholder: $localize`:@@deviceNotes-defaultPlaceholder:Search user name...`,
		addedBy: $localize`:@@deviceNotes-filterLabelAddedBy:Added By`,
		notes: $localize`:@@deviceNotes-filterLabelNote:Notes`,
		dateAdded: $localize`:@@deviceNotes-filterLabelDateAdded:Date Added`,
		noteType: $localize`:@@deviceNotes-filterLabelNoteType:Note Type`,
	};
	noteTypeOptions = [
		{
			label: $localize`:@@deviceNotes-filterOptionInstallation:Installation`,
			value: 'Installation',
		},
		{
			label: $localize`:@@deviceNotes-filterOptionManualEnd:Manual End`,
			value: 'ManualEnd',
		},
		{
			label: $localize`:@@deviceNotes-filterOptionVisit:Visit`,
			value: 'AppVisit',
		},
		{
			label: $localize`:@@deviceNotes-filterOptionWeb:Web`,
			value: 'Web',
		},
	];

	private filterSubscription?: Subscription;
	private resizeTimer: any;

	getAllDeviceNotesLoadingKey = ApiLoadingKeys.getAllDeviceNotes;
	finishedLoadingInitData: boolean = false;

	constructor(
		private readonly dialog: MatDialog,
		private readonly route: ActivatedRoute,
		private readonly apiService: ApiService,
		public readonly loadingService: LoadingIndicatorService,
		readonly timeZoneService: TimeZoneService,
		private readonly router: Router
	) {}

	async ngAfterViewInit(): Promise<void> {
		this.filterSubscription = this.dataSet.queryParams
			.pipe(
				map((filters) =>
					filters.map((filter) =>
						filter.value instanceof Date
							? {
									...filter,
									value: formatDate(filter.value, 'yyyy-MM-dd', 'en-US'),
								}
							: filter
					)
				),
				mergeMap(async (filter) => {
					// US10151: the parameter 'alternateNoteTypeLabels' displays a lowercase,
					// more readable name for types of notes
					const result = await this.apiService.getDeviceNotes(
						[
							...filter,
							{
								param: 'alternateNoteTypeLabels',
								value: true,
							} as QueryParam,
						],
						this.deviceId
					);
					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((result) => {
				this.dataSource.data = result.data;

				// Once the elements in the table load in, set their overflow statuses
				setTimeout(() => {
					for (let i = 0; i < this.dataSource.data.length; i++) {
						this.setOverflowContainer(i);
					}
				}, 1000);
			});

		// If there's a window resize, check if note overflow containers are needed
		window.onresize = () => {
			clearTimeout(this.resizeTimer);
			this.resizeTimer = setTimeout(() => {
				for (let i = 0; i < this.dataSource.data.length; i++) {
					this.setOverflowContainer(i);
				}
			}, 300);
		};
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
		clearTimeout(this.resizeTimer);
		this.resizeTimer = undefined;
		for (let i = 0; i < this.dataSource.data.length; i++) {
			if (this.dataSource.data[i].arrowListener) {
				const noteContainter = document.getElementById('note-container-' + i);
				if (noteContainter) {
					noteContainter.removeEventListener(
						'click',
						this.dataSource.data[i].arrowListener!
					);
				}
				this.dataSource.data[i].arrowListener = undefined;
			}
		}
	}

	showPhotos(photos: DeviceNotePhoto[]): void {
		const photoGalleryItems = photos.map((it) => ({
			photoUrl: it.photoUrl,
			caption: it.caption,
		}));
		this.dialog.open(PhotoGalleryComponent, { data: photoGalleryItems });
	}

	navigateToAddDeviceNote(): void {
		this.router.navigate(['/add-note'], {
			queryParams: { deviceId: this.deviceId },
		});
	}

	setOverflowContainer(i: number) {
		const noteContainter = document.getElementById('note-container-' + i);
		const noteText = document.getElementById('note-text-' + i);
		const downCarrot = document.getElementById('note-down-carrot-' + i);
		const upCarrot = document.getElementById('note-up-carrot-' + i);

		// Reset everything to defaults
		if (this.dataSource.data[i].arrowListener) {
			this.dataSource.data[i].contractedNotes = undefined;

			if (noteContainter) {
				noteContainter.removeEventListener(
					'click',
					this.dataSource.data[i].arrowListener!
				);
			}
			if (noteText) {
				noteText.style['webkitLineClamp'] = '3';
			}
			if (downCarrot) {
				downCarrot.style.width = '0';
				downCarrot.style.visibility = 'hidden';
			}
			if (upCarrot) {
				upCarrot.style.width = '0';
				upCarrot.style.visibility = 'hidden';
			}
		}

		// Check if an element needs an overflow container
		if (noteText && noteText.offsetHeight < noteText.scrollHeight) {
			this.dataSource.data[i].contractedNotes = true;
			if (noteContainter) {
				noteContainter.style.cursor = 'pointer';
				const funct = () => this.onClickResize(i);
				this.dataSource.data[i].arrowListener = funct;
				noteContainter.addEventListener('click', funct);
			}
			if (downCarrot) {
				downCarrot.style.width = '15px';
				downCarrot.style.visibility = 'visible';
			}
		} else {
			if (noteContainter) {
				noteContainter.style.cursor = 'default';
			}
		}
	}

	// Dictates behavior of overflow container - will be able to open/close large notes that overflow
	onClickResize(i: number) {
		const noteText = document.getElementById('note-text-' + i);
		const noteContainter = document.getElementById('note-container-' + i);
		const downCarrot = document.getElementById('note-down-carrot-' + i);
		const upCarrot = document.getElementById('note-up-carrot-' + i);

		if (noteText) {
			this.dataSource.data[i].contractedNotes = !(this.dataSource.data[i]
				.contractedNotes
				? true
				: false);

			if (this.dataSource.data[i].contractedNotes === false) {
				noteText.style['webkitLineClamp'] = 'none';
				if (upCarrot) {
					upCarrot.style.width = '15px';
					upCarrot.style.visibility = 'visible';
				}
				if (downCarrot) {
					downCarrot.style.width = '0';
					downCarrot.style.visibility = 'hidden';
				}
			} else {
				noteText.style['webkitLineClamp'] = '3';
				if (downCarrot) {
					downCarrot.style.width = '15px';
					downCarrot.style.visibility = 'visible';
				}
				if (upCarrot) {
					upCarrot.style.width = '0';
					upCarrot.style.visibility = 'hidden';
				}
			}
		}

		if (noteContainter) {
			noteContainter.style.cursor = 'pointer';
		}
	}

	getApiService() {
		return this.apiService;
	}

	rowId(row: {
		deviceNote: DeviceNote;
		user: User;
		deviceNotePhotos: DeviceNotePhoto[];
	}): string {
		return (
			row.deviceNote.appVisitNoteId ||
			row.deviceNote.appVisitPhotoId ||
			'unknown-id'
		);
	}
}
